import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { Observable, finalize, map, shareReplay, tap, throwError } from 'rxjs';
import { AnalyzeIntakeResponse } from '../../features/intake-selector/intake.models';
import { BlueprintService } from '../../features/architecture/blueprint/blueprint.service';
import {
  GenerateBlueprintRequest,
  GenerateBlueprintResponse,
} from '../../features/architecture/blueprint/blueprint.models';
import { UiSpecService } from '../../features/ui-feedback/ui-spec.service';
import { UiSpecResult } from '../../features/ui-feedback/ui-spec.models';
import { ProjectApiService } from './project-api.service';
import {
  ArtifactDownloadResponse,
  ProjectArtifact,
  ProjectArtifactKind,
  ProjectDetail,
  ProjectSummary,
} from './models/project.models';

/**
 * ProjectSessionService
 *
 * Root-provided singleton holding the in-memory project state produced by
 * document intake analysis, plus the progressive-unlock cursor for the
 * sidebar workflow stages.
 *
 * The in-memory state is now a cache over durable project data. A hard reload
 * can restore a project from `GET /projects/{id}` and rehydrate the stage
 * cursor, analysis, blueprint result and UI spec from the backend.
 */
@Injectable({
  providedIn: 'root',
})
export class ProjectSessionService {
  /** The project currently loaded into the wizard, or null when none is active. */
  readonly projectId = signal<string | null>(null);

  /**
   * Artifact kinds restored from the backend that are out of date with respect
   * to something they were generated from. They are still shown; this is what
   * lets the page say so rather than presenting them as current.
   */
  readonly staleKinds = signal<ReadonlySet<ProjectArtifactKind>>(new Set());

  /** True when a project was requested but could not be loaded. */
  readonly hydrationFailed = signal<boolean>(false);

  /**
   * Records that saved work could not be loaded, so the page can say so rather
   * than showing an empty wizard that looks identical to having lost it.
   */
  markHydrationFailed(projectId: string): void {
    this.projectId.set(projectId);
    this.hydrationFailed.set(true);
  }

  /** True when the restored blueprint no longer reflects the current brief. */
  isStale(kind: ProjectArtifactKind): boolean {
    return this.staleKinds().has(kind);
  }

  /** The most recent document intake analysis result, or null if none yet. */
  readonly analysis = signal<AnalyzeIntakeResponse | null>(null);

  /**
   * Job id of the Solution Blueprint auto-triggered when intake analysis
   * finished, or null if no intake has completed in this session.
   */
  readonly blueprintJobId = signal<string | null>(null);

  /**
   * The completed blueprint result, cached once polling resolves so
   * re-visiting the Architecture page within the same session doesn't
   * re-poll. Cleared whenever a new analysis is set.
   */
  readonly blueprintResult = signal<GenerateBlueprintResponse | null>(null);

  /**
   * Job id of the UI specification auto-triggered when intake analysis
   * finished, or null if no intake has completed in this session (or the
   * chaining failed). Reassigned after a refinement, so it always points at
   * the version currently on screen.
   */
  readonly uiSpecJobId = signal<string | null>(null);

  /**
   * The completed UI specification, cached once polling resolves so
   * re-visiting the UI Feedback page within the same session doesn't re-poll.
   * Cleared whenever a new analysis is set.
   */
  readonly uiSpecResult = signal<UiSpecResult | null>(null);

  /**
   * True once a job errored (or reported `persisted: false`) without ever
   * producing a result. Distinct from simply having no job — this stops the
   * background-generating indicator from spinning forever after a failure
   * that a retry (via revisiting the page) is needed to clear.
   */
  readonly blueprintFailed = signal<boolean>(false);
  readonly uiSpecFailed = signal<boolean>(false);

  /** True while the Solution Blueprint job is in flight with no result yet. */
  readonly blueprintGenerating = computed(
    () => this.blueprintJobId() !== null && this.blueprintResult() === null && !this.blueprintFailed()
  );

  /** True while the UI specification job is in flight with no result yet. */
  readonly uiSpecGenerating = computed(
    () => this.uiSpecJobId() !== null && this.uiSpecResult() === null && !this.uiSpecFailed()
  );

  /** True while anything for the active project is still generating in the background. */
  readonly isGenerating = computed(() => this.blueprintGenerating() || this.uiSpecGenerating());

  /**
   * 0-based index of the furthest sidebar stage unlocked so far.
   * Monotonic — only ever increases within a session.
   */
  readonly maxUnlockedStage = signal<number>(0);

  private readonly injector = inject(Injector);

  /** The one in-flight blueprint poll, shared so no caller starts a second one for the same job. */
  private blueprintPoll$: Observable<GenerateBlueprintResponse> | null = null;

  /** The one in-flight UI spec poll, shared so no caller starts a second one for the same job. */
  private uiSpecPoll$: Observable<UiSpecResult> | null = null;

  /**
   * Drops everything back to a clean slate before a genuinely new intake
   * starts.
   *
   * This is a root-provided singleton, so without this a "Start New Project"
   * click would carry the previous project's id forward in memory. The next
   * analysis would then include that stale id on the request (see
   * `IntakeService.currentProjectId`), which the backend reads as "attach
   * this to the existing project" — no new `projects` row is created, and the
   * new artifacts silently land on the old project instead.
   */
  reset(): void {
    this.projectId.set(null);
    this.hydrationFailed.set(false);
    this.staleKinds.set(new Set());
    this.analysis.set(null);
    this.blueprintPoll$ = null;
    this.blueprintJobId.set(null);
    this.blueprintResult.set(null);
    this.blueprintFailed.set(false);
    this.uiSpecPoll$ = null;
    this.uiSpecJobId.set(null);
    this.uiSpecResult.set(null);
    this.uiSpecFailed.set(false);
    this.maxUnlockedStage.set(0);
  }

  /**
   * Resolves the API client only when it is actually needed.
   *
   * ProjectSessionService is injected by nearly every wizard component as a
   * lightweight state holder. Depending on ProjectApiService at construction
   * would drag HttpClient into all of them, so resolution is deferred to the
   * two methods that genuinely talk to the backend.
   */
  private api(): ProjectApiService {
    return this.injector.get(ProjectApiService);
  }

  private blueprintApi(): BlueprintService {
    return this.injector.get(BlueprintService);
  }

  private uiSpecApi(): UiSpecService {
    return this.injector.get(UiSpecService);
  }

  /**
   * Polls the active blueprint job, sharing the one in-flight HTTP request
   * across every caller. The background tracker started from `setAnalysis`
   * or a hydration and the Architecture page's own poll both attach to the
   * same stream here, so the same job is never polled twice at once.
   *
   * Returns null when there is no active blueprint job to poll.
   */
  pollBlueprint(): Observable<GenerateBlueprintResponse> | null {
    const jobId = this.blueprintJobId();
    if (!jobId) {
      return null;
    }
    if (!this.blueprintPoll$) {
      this.blueprintPoll$ = this.blueprintApi()
        .pollBlueprint(jobId)
        .pipe(
          finalize(() => {
            this.blueprintPoll$ = null;
          }),
          shareReplay(1)
        );
    }
    return this.blueprintPoll$;
  }

  /** Same sharing behaviour as {@link pollBlueprint}, for the UI specification job. */
  pollUiSpec(): Observable<UiSpecResult> | null {
    const jobId = this.uiSpecJobId();
    if (!jobId) {
      return null;
    }
    if (!this.uiSpecPoll$) {
      this.uiSpecPoll$ = this.uiSpecApi()
        .pollUiSpec(jobId)
        .pipe(
          finalize(() => {
            this.uiSpecPoll$ = null;
          }),
          shareReplay(1)
        );
    }
    return this.uiSpecPoll$;
  }

  /**
   * Loads a project from the backend and rehydrates the cached wizard state.
   *
   * The project brief and the persisted project_context artifact restore the
   * intake analysis; current sbp_content + sbp_docx artifacts restore the
   * blueprint download state; and a current ui_spec artifact restores the UI
   * feedback page.
   */
  hydrateProject(projectId: string): Observable<ProjectDetail> {
    return this.api()
      .getProject(projectId)
      .pipe(tap((project) => this.applyProjectDetail(project)));
  }

  /** Lists the requester's projects for the home screen's resume list. */
  listProjects(): Observable<ProjectSummary[]> {
    return this.api().listProjects();
  }

  /**
   * Applies an intake analysis, adopting the project id the backend attached
   * to the job that produced it — `POST /intake/analyze` creates the project
   * itself on the very first call (when no `project_id` was sent), so there
   * is no separate `POST /projects` round trip to make here.
   *
   * `projectId` comes from the job envelope (`IntakeOutcome.projectId`), not
   * from `response` — `null` is a legitimate outcome meaning this analysis
   * was not saved to a project, not a value to paper over with a locally
   * generated fallback.
   *
   * The id must land before `setAnalysis`: it unlocks stage 1, and that
   * unlock is persisted via `unlockThrough` -> `persistStageCursor`, which is
   * a no-op without a project id already set.
   */
  applyAnalysisResult(response: AnalyzeIntakeResponse, projectId: string | null): void {
    // A failed hydration left a project id behind (see markHydrationFailed)
    // but nothing actually loaded, so it must not block a fresh analysis
    // from adopting its own id here.
    if (projectId && (this.projectId() === null || this.hydrationFailed())) {
      this.projectId.set(projectId);
      this.hydrationFailed.set(false);
    }
    this.setAnalysis(response);
  }

  /**
   * Stores the intake analysis result and unlocks the Classification stage (index 1).
   */
  setAnalysis(response: AnalyzeIntakeResponse): void {
    this.analysis.set(response);
    this.blueprintPoll$ = null;
    this.blueprintJobId.set(response.blueprint_job_id ?? null);
    this.blueprintResult.set(null);
    this.blueprintFailed.set(false);
    this.uiSpecPoll$ = null;
    this.uiSpecJobId.set(response.ui_spec_job_id ?? null);
    this.uiSpecResult.set(null);
    this.uiSpecFailed.set(false);
    this.unlockThrough(1);
  }

  /** Caches the completed blueprint result once polling resolves. */
  setBlueprintResult(response: GenerateBlueprintResponse): void {
    this.blueprintResult.set(response);
  }

  /**
   * Caches a UI specification once polling resolves.
   *
   * A refinement creates a new backend job, so its id is recorded alongside
   * the result — any further refinement is based on the version now on screen,
   * not the original one.
   */
  setUiSpecResult(result: UiSpecResult, jobId?: string): void {
    this.uiSpecResult.set(result);
    if (jobId) {
      this.uiSpecJobId.set(jobId);
    }
  }

  /**
   * A time-limited download link for the current version of a project
   * artifact, opened directly as a plain navigation (never an XHR, so it
   * never touches S3 CORS) — same approach as `BlueprintService.download`.
   *
   * This is what lets a restored project's files be re-downloaded: the job
   * that originally produced them may be long gone, but the artifact it
   * persisted is still on the project and this route serves it directly.
   */
  downloadArtifact(kind: ProjectArtifactKind): Observable<void> {
    const projectId = this.projectId();
    if (!projectId) {
      return throwError(() => new Error('downloadArtifact requires an active project.'));
    }
    return this.api()
      .downloadArtifact(projectId, kind)
      .pipe(
        tap((response: ArtifactDownloadResponse) => {
          window.open(response.download_url, '_blank');
        }),
        map(() => undefined)
      );
  }

  /** Returns an absolute route for a wizard stage, preserving the project prefix when active. */
  stageRoute(path: string): string[] {
    const route = path.replace(/^\/+/, '');
    const projectId = this.projectId();
    if (projectId) {
      return ['/projects', projectId, route];
    }
    return [`/${route}`];
  }

  /**
   * Unlocks stages up through and including `index`. No-op if `index` is not
   * greater than the current maxUnlockedStage — the cursor never decreases.
   *
   * The new position is also sent to the backend, because nothing else records
   * it: every artifact is generated automatically during intake, so what is
   * stored shows what the system produced, never how far the requester walked.
   * The write is fire-and-forget — failing to save the cursor must not block
   * someone moving to the next stage, and the next advance will carry it.
   */
  unlockThrough(index: number): void {
    if (index > this.maxUnlockedStage()) {
      this.maxUnlockedStage.set(index);
      this.persistStageCursor(index, index);
    }
  }

  private persistStageCursor(currentStage: number, maxUnlockedStage: number): void {
    const projectId = this.projectId();
    if (!projectId) {
      // No project yet, so there is nothing to save against. Checked before
      // the API client is resolved, which keeps unsaved wizard flows - and the
      // component tests that exercise them - free of HTTP entirely.
      return;
    }
    this.api()
      .updateStageCursor(projectId, currentStage, maxUnlockedStage)
      .subscribe({
        error: () => {
          // Deliberately swallowed: see above.
        },
      });
  }

  /**
   * True when stage `index` is unlocked (reachable) given the current cursor.
   *
   * Requirements Intake (index 0) is the one exception: `createProject()`
   * only ever fires once intake analysis has succeeded, so an active project
   * id already means intake is done — redoing it doesn't apply.
   */
  isUnlocked(index: number): boolean {
    if (index === 0) {
      return this.projectId() === null;
    }
    return index <= this.maxUnlockedStage();
  }

  private applyProjectDetail(detail: ProjectDetail): void {
    this.projectId.set(detail.project_id);
    this.hydrationFailed.set(false);
    this.maxUnlockedStage.set(detail.max_unlocked_stage);
    this.staleKinds.set(
      new Set(
        detail.artifacts
          .filter((artifact) => artifact.status === 'current' && artifact.stale)
          .map((artifact) => artifact.kind)
      )
    );
    this.analysis.set(this.buildAnalysis(detail));
    this.blueprintPoll$ = null;
    this.blueprintFailed.set(false);
    this.blueprintResult.set(this.buildBlueprintResult(detail));
    this.blueprintJobId.set(this.activeJobId(detail, 'blueprint'));
    this.uiSpecPoll$ = null;
    this.uiSpecFailed.set(false);
    this.uiSpecResult.set(this.buildUiSpecResult(detail));
    this.uiSpecJobId.set(this.activeJobId(detail, 'ui_spec'));
  }

  /**
   * Finds a still-running job of `kind` on the durable project, so revisiting
   * doesn't lose in-flight generation the way hard-nulling the job ids used
   * to. A missing `active_jobs` (older API) is treated the same as `[]` —
   * nothing running.
   */
  private activeJobId(detail: ProjectDetail, kind: string): string | null {
    const job = detail.active_jobs?.find((candidate) => candidate.kind === kind);
    return job ? job.job_id : null;
  }

  private buildAnalysis(detail: ProjectDetail): AnalyzeIntakeResponse | null {
    const brief = this.asBlueprintRequest(detail.brief);
    const projectContext = this.currentArtifact(detail, 'project_context');

    if (!brief || !projectContext?.content) {
      return null;
    }

    return {
      analysis: projectContext.content as AnalyzeIntakeResponse['analysis'],
      brief,
      extracted_characters: 0,
      blueprint_job_id: '',
      ui_spec_job_id: null,
    };
  }

  private buildBlueprintResult(detail: ProjectDetail): GenerateBlueprintResponse | null {
    const brief = this.asBlueprintRequest(detail.brief);
    const blueprintContent = this.currentArtifact(detail, 'sbp_content');
    const blueprintDocument = this.currentArtifact(detail, 'sbp_docx');

    if (!brief || !blueprintContent?.content || !blueprintDocument?.s3_key) {
      return null;
    }

    const content = blueprintContent.content as { open_items?: string[] };
    return {
      filename: this.blueprintFilename(blueprintDocument.s3_key),
      document_s3_key: blueprintDocument.s3_key,
      open_items: content.open_items ?? [],
    };
  }

  private buildUiSpecResult(detail: ProjectDetail): UiSpecResult | null {
    const uiSpec = this.currentArtifact(detail, 'ui_spec');
    if (!uiSpec?.content) {
      return null;
    }

    return uiSpec.content as UiSpecResult;
  }

  private currentArtifact(detail: ProjectDetail, kind: ProjectArtifact['kind']): ProjectArtifact | null {
    // Stale artifacts are restored too. Stale means the blueprint answers an
    // older brief, not that it is missing — hiding it would make a reload look
    // exactly like the data loss this persistence work exists to end.
    const current = detail.artifacts
      .filter((artifact) => artifact.kind === kind && artifact.status === 'current')
      .sort((a, b) => b.version - a.version)[0];
    return current ?? null;
  }

  private asBlueprintRequest(value: unknown): GenerateBlueprintRequest | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    return value as GenerateBlueprintRequest;
  }

  private blueprintFilename(s3Key: string): string {
    // Taken from the stored object rather than rebuilt from the project name,
    // so the download is named what it actually is.
    const name = s3Key.split('/').pop();
    return name && name.length > 0 ? name : 'Solution_Blueprint.docx';
  }
}
